<app-general-login-flow title="Authentifizierung" [showSpinner]="showSpinner" [keybackup]="true">
  <div class="body">
    <p class="keys-for-chat">Schlüssel für Ihre Chats</p>
    @if (isVerifying) {
      <ng-container *ngTemplateOutlet="verifyContent" />
    } @else {
      <ng-container *ngTemplateOutlet="resultContent" />
    }
    @if (!isVerifying && isSuccess) {
      <div class="link-wrapper footer-content">
        <app-button
          (click)="navigateToChat()"
          (keydown.enter)="navigateToChat()"
          [dataTestId]="testIds.chatNavigation"
          [isDisabled]="!hasKeybackup && !isPasswordCopied"
          [isPrimary]="false"
          [matTooltip]="
            !hasKeybackup && !isPasswordCopied
              ? 'Um fortzufahren speichern Sie Ihren Schlüssel mit Klick auf die Schlüsselzeile'
              : ''
          "
          class="navigation-link"
          text="Zu den Chats" />
        @if (!hasKeybackup) {
          <p class="save-info">Bitte speichern Sie den Schlüssel an einem sicheren Ort, wie dem Passwortmanager.</p>
        }
      </div>
    }
  </div>
</app-general-login-flow>

<ng-template #verifyContent>
  <div class="banner-wrapper">
    @if (!hasKeybackup) {
      <app-alert-box title="Hinweis">
        <p>Bewahren Sie den Schlüssel an einem sicheren Ort auf, zum Beispiel in einem Passwort‑Manager.</p>
      </app-alert-box>

      <app-alert-box title="Wichtig" type="warning">
        <p>
          Aus Sicherheitsgründen darf der Schlüssel nicht Ihrem TI-M Passwort entsprechen. <br />Geht der Schlüssel
          verloren, können bestehende Chats nicht wiederhergestellt werden – auch nicht durch Administratoren.
        </p>
      </app-alert-box>
    } @else {
      <app-alert-box title="Hinweis">
        <p>
          Geben Sie Ihren Schlüssel ein, um die verschlüsselten Chatverläufe auf diesem Gerät zu entsperren. Der
          Schlüssel wurde bei der ersten Nutzung des TI‑Messengers eingerichtet – entweder von Ihnen oder von einer
          anderen Person, die diesen Account nutzt. Nur mit diesem Schlüssel erhalten Sie Zugriff auf Ihre vertraulichen
          Nachrichten.
        </p>
      </app-alert-box>
    }
  </div>
  <ng-container *ngTemplateOutlet="verifyForm" />
</ng-template>

<ng-template #resultContent>
  <div class="banner-wrapper">
    <div class="body-wrapper">
      @if (isSuccess) {
        @if (hasKeybackup) {
          <app-alert-box [attr.data-testid]="testIds.verifySuccess" title="Erfolgreich" type="success">
            Ihr Schlüssel wurde erfolgreich verifiziert.
          </app-alert-box>
        }
        @if (!hasKeybackup) {
          <app-alert-box [attr.data-testid]="testIds.initialVerifySuccess" title="Wichtig" type="warning">
            <p>Dieser Schlüssel ist jetzt registriert und wird vollständig zum Abspeichern angezeigt.</p>
          </app-alert-box>
          <app-alert-box title="Hinweis"
            ><p>
              Geht der Schlüssel verloren, können bestehende Chats nicht wiederhergestellt werden – auch nicht durch
              Administratoren.
            </p>
          </app-alert-box>
        }
      } @else {
        <app-alert-box [attr.data-testid]="testIds.verifyFailure" class="banner-text" title="Fehler" type="error">
          {{
            hasKeybackup
              ? 'Es scheint ein Problem bei Ihrer Verifikation zu geben. Bitte versuchen Sie es erneut.'
              : 'Es scheint ein Problem bei der Vergabe Ihres Schlüssels aufgetreten zu sein. Bitte versuchen Sie es erneut.'
          }}
        </app-alert-box>
      }
    </div>
  </div>

  @if (isSuccess) {
    <p class="key-label">Schlüssel</p>
    <button class="password-success" (click)="copyInput()" [attr.data-testid]="testIds.btnCopypassword">
      <div class="icon-wrapper">
        <p [attr.data-testid]="testIds.savedPassword" class="password-value">
          {{ verificationForm.controls.password.value }}
        </p>
      </div>
      <div class="copy-text">
        <mat-icon class="icon-interaction" matSuffix svgIcon="duplicate"></mat-icon>
        <p>Kopieren</p>
      </div>
    </button>
  } @else {
    <ng-container *ngTemplateOutlet="verifyForm" />
  }
</ng-template>

<ng-template #verifyForm>
  <form [formGroup]="verificationForm" (ngSubmit)="setKeyBackup()" #passwordForm="ngForm">
    <div class="form-field-wrapper">
      <label for="key">Schlüssel <span class="required">*</span></label>

      <mat-form-field
        class="password-input"
        [class.keybackup-error]="verificationForm.invalid && passwordForm.submitted"
        subscriptSizing="dynamic">
        <mat-icon matPrefix svgIcon="lock"></mat-icon>

        <input
          id="key"
          matInput
          formControlName="password"
          placeholder="Schlüssel eingeben"
          [type]="isPasswordVisible ? 'text' : 'password'"
          [attr.data-testid]="testIds.fieldInputVerificationpassword" />

        @if (isPasswordVisible) {
          <mat-icon
            class="icon-interaction"
            matSuffix
            svgIcon="duplicate"
            [attr.data-testid]="testIds.btnCopypassword"
            (click)="copyInput()"
            (keydown)="$event.stopPropagation()"></mat-icon>
        }

        <mat-icon
          class="icon-interaction"
          matSuffix
          [svgIcon]="isPasswordVisible ? 'eye' : 'eye-slash'"
          (click)="isPasswordVisible = !isPasswordVisible"
          (keydown)="$event.stopPropagation()"
          [attr.data-testid]="testIds.passwordVisibilityToggle"></mat-icon>
      </mat-form-field>
      @if (!hasKeybackup && (verificationForm.dirty || verificationForm.touched)) {
        <app-password-hint [control]="verificationForm.controls['password']"></app-password-hint>
      }
    </div>

    <div class="setup-btn-wrapper">
      <button
        class="setup-btn-tertiary"
        type="button"
        (click)="tertiaryBtnAction()"
        [attr.data-testid]="tertiaryBtnTestIds">
        {{ tertiaryBtnText }}
      </button>
      <button
        [attr.data-testid]="testIds.btnSubmitVerification"
        [disabled]="verificationForm.invalid"
        class="setup-btn"
        type="submit">
        {{ hasKeybackup ? 'Bestätigen' : 'Schlüssel einrichten' }}
      </button>
    </div>
  </form>
</ng-template>
